<template>
  <validation-provider
    tag="div"
    name="birth_date"
    :rules="rules"
    mode="passive"
    v-slot="{ errors }"
  >
    <form-label for="birth_date">
      Date of Birth
      <span class="text-xs" v-if="internalValue">(MM/DD/YYYY)</span>
      <span class="leading-none text-red-400">*</span>
    </form-label>
    <form-input
      id="birth_date"
      type="tel"
      v-model="internalValue"
      v-mask="'##/##/####'"
      placeholder="MM/DD/YYYY"
      @input="onInput"
    ></form-input>
    <form-alert-msg v-if="errors[0]">{{ errors[0] }}</form-alert-msg>
  </validation-provider>
</template>

<script>
import { FormInput, FormLabel, FormAlertMsg } from "@/components/Form/";
import { ValidationProvider, extend } from "vee-validate";
import { required, length } from "vee-validate/dist/rules";
import { VueMaskDirective } from "v-mask";
import { isValidDate, calcAge } from "@/helper";
extend("required", { ...required, message: "This field is required." });

// Extend the vee-validate library to validate the date length
extend("date_length", {
  ...length,
  message: "This field must be a valid date.",
});

// Extend the vee-validate library to validate the date field
extend("date", {
  validate(value) {
    return isValidDate(value);
  },
  message: "This field must be a valid date",
});

// Initialize the age difference variable
let ageDiff = 0;
let ageType = "";

// Extend the vee-validate library to validate the age type
extend("age_type", {
  validate(value, { type, child_age_limit }) {
    ageDiff = calcAge(value);
    const age_limit = parseInt(child_age_limit, 10);
    ageType = ageDiff <= age_limit ? "child" : "adult";
    return type === ageType;
  },
  params: ["type", "child_age_limit"],
  message: (fieldName, placeholders) => {
    if (ageType === "adult") {
      return `This field must be within "${placeholders.type}" age type. Your current age is ${ageDiff} years, which exceeds the child age limit of ${placeholders.child_age_limit} years.`;
    }
    return `This field must be within "${placeholders.type}" age type. Your current age is ${ageDiff} years, which is below the adult age threshold of over ${placeholders.child_age_limit} years.`;
  },
});

export default {
  components: {
    ValidationProvider,
    FormInput,
    FormLabel,
    FormAlertMsg,
  },
  directives: {
    mask: VueMaskDirective,
  },
  props: ["value", "controls"],
  data() {
    return {
      internalValue: "",
    };
  },
  computed: {
    childAgeLimit() {
      // If location setting child_age_limit is set, use it, otherwise use the global setting
      if (this.controls?.locations?.settings?.child_age_limit) {
        return this.controls?.locations?.settings?.child_age_limit;
      }

      return this.$store?.state?.settings?.child_age_limit || 12;
    },
    rules() {
      const child_age_limit = this.childAgeLimit;

      const field_age_type = this.controls?.age_type;

      const service_age_type =
        this.controls?.services?.age_type || field_age_type;

      const age_type = this.controls?.sameday
        ? service_age_type
        : field_age_type;

      return `required|date|date_length:10|age_type:${age_type},${child_age_limit}`;
    },
  },
  watch: {
    value: {
      handler(val) {
        this.internalValue = val;
      },
      immediate: true,
    },
    childAgeLimit: {
      handler(newval, oldval) {
        // console.log("childAgeLimit", newval, oldval);
        if (newval !== oldval) {
          this.$emit("input:field", "");
        }
      },
    },
  },
  methods: {
    onInput(val) {
      this.$emit("input:field", val);
    },
  },
};
</script>
